import React, { useEffect, useRef, useState } from "react";
import Sound from "../../assets/sound/music.mp3";
import EmbudoLoading from '../../components/ElEmbudo/EmbudoLoading/EmbudoLoading';
import GifStage from '../../components/ElEmbudo/GifStage/GifStage';
import IntroCinematic from "../../components/ElEmbudo/IntroCinematic/IntroCinematic";
import WaitScreen from '../../components/ElEmbudo/WaitScreen/WaitScreen';
import ErrorPage from '../../components/shared/ErrorPage';
import HttpService from '../../config/http/http.service';
import { colorMap } from '../../constants/colors';
import { endpoints } from '../../constants/endpoints';
import { parseTimeToDate } from '../../utils';

interface Props {
  onGameEnd?: () => void;
}

interface Result {
  number: number;
  colorCode: string;
}

const ElEmbudo: React.FC<Props> = ({onGameEnd}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLotteryClosed, setLotteryCloses] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [showWaitScreen, setShowWaitScreen] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [remainingMilliseconds, setRemainingMilliseconds] = useState<number | null>(null);
  const [result, setResult] = useState<Result[]>([]);
  const [intro, setIntro] = useState<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const httpService = new HttpService();

  const fetchData = async (resultPerPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await httpService.post(endpoints.EMBUDO_GET_RESULTS, { token, ResultPerPage: resultPerPage }, {});
      if (!response) return;

      const { mainObject }: any = response;
      const { activeLottery: { startTime, closeTime }, lotteryResults, isActive } = mainObject;
      
      if (!isActive) setLotteryCloses(false);

      setData({ startTime, closeTime, lotteryResults });

      const numberArray = lotteryResults[0].numberResult.split("-").map(Number);
      const colorArray = lotteryResults[0].colorResult.split("-").map((color: string) => colorMap[color]);
      const combinedResults = numberArray.map((number: number, index: number) => ({
        number,
        colorCode: colorArray[index],
      }));

      setResult(combinedResults);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLotteryCloses(true);
      setError("Error loading data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const evaluateDates = () => {
    if (!data) return;
    const now = new Date();
    const startTime = parseTimeToDate(data.startTime);
    const closeTime = parseTimeToDate(data.closeTime);

    if (now >= startTime && now <= closeTime) {
      const remaining = closeTime.getTime() - now.getTime();
      setRemainingMilliseconds(remaining);
      setShowWaitScreen(true);
    } else {
      setShowWaitScreen(false);
      setTimeout(() => {
        fetchData(1);
      }, 35000);
    }
  };

  const handleVideoEnd = () => {
    const now = new Date();
    const closeTime = parseTimeToDate(data.closeTime);
    const remaining = closeTime.getTime() - now.getTime();

    if (remaining > 0) {
      setRemainingMilliseconds(remaining);
      setShowResult(false);
      setShowWaitScreen(true);
    } else {
      fetchData(1);

      if(onGameEnd)
        onGameEnd();
    }
  };

  const syncAudioWithVideo = () => {
    if (intro && audioRef.current) {
      const video = intro;
      const audio = audioRef.current;
      audio.currentTime = video.currentTime;
      audio.play().catch(error => console.warn("Error playing audio:", error));
    }
  };

  useEffect(() => {
    fetchData(1);
  }, []);

  useEffect(() => {
    if (data) {
      evaluateDates();
    }
  }, [data]);

  useEffect(() => {
    if (remainingMilliseconds !== null && showWaitScreen) {
      const timer = setTimeout(() => {
        setShowWaitScreen(false);
      }, remainingMilliseconds);

      return () => clearTimeout(timer);
    }
  }, [remainingMilliseconds, showWaitScreen]);

  useEffect(() => {
    if (intro) {
      const handleTimeUpdate = () => {
        if (intro.currentTime > 25) {
          setShowResult(true);
        }
      };
      intro.addEventListener("timeupdate", handleTimeUpdate);
      intro.addEventListener("play", syncAudioWithVideo);

      return () => {
        intro.removeEventListener("timeupdate", handleTimeUpdate);
        intro.removeEventListener("play", syncAudioWithVideo);
      };
    }
  }, [intro]);

  useEffect(() => {
    const initializeGame = async () => {
      const audioElement = audioRef.current;
      if (audioElement) {
        const playPromise = audioElement.play();
        if (playPromise) {
          playPromise.catch(error => console.log("Playback prevented", error));
        }
      }
    };

    initializeGame();
  }, []);

  if (error || isLotteryClosed) {
    return <ErrorPage />;
  }

  return (
    <>
      {loading ? (
        <EmbudoLoading />
      ) : showWaitScreen && remainingMilliseconds !== null ? (
        <WaitScreen timeInMilliseconds={remainingMilliseconds} />
      ) : (
        <div className="container-fluid bg-black h-100">
          <IntroCinematic onPlaying={setIntro} style={{ display: showResult ? "none" : "block" }} />
          <GifStage
            results={result}
            style={{ position: "fixed", aspectRatio: "16/9", zIndex: showResult ? 1000 : -1 }}
            initGame={showResult}
            onEnded={handleVideoEnd}
          />
          <audio ref={audioRef} crossOrigin="anonymous" preload="auto" autoPlay>
            <source src={Sound} type="audio/mp3" />
          </audio>
        </div>
      )}
    </>
  );
};

export default ElEmbudo;
